import React, { useMemo } from "react";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    LabelList,
} from "recharts";


// Datos tal cual llegan del backend
export type BackendItemMaintenance = {
    vehicleId: string;
    plateNumber: string;
    brand: string;
    model: string;
    totalMaintenanceCost: string | number; // € acumulados en el periodo
};


export type PropsMaintenance = {
    title?: string;
    periodLabel: string;
    items?: BackendItemMaintenance[];
    loading?: boolean;
    error?: string | null;
    height?: number; // px (default 320)
    className?: string;
    color?: string; // color de barra
    maxRows?: number; // top N (default 6)
    sort?: "asc" | "desc"; // ordenar por coste total (default desc)
};


const fmtCurrency = (n: number) =>
    new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n);

export const AccumulatedMaintenanceCostsPerVehicleRanking: React.FC<PropsMaintenance> = ({
                                                                                             title = "Coste de mantenimiento por vehículo",
                                                                                             periodLabel,
                                                                                             items = [],
                                                                                             loading = false,
                                                                                             error = null,
                                                                                             height = 320,
                                                                                             className = "",
                                                                                             color = "#6b7280", // gris sobrio
                                                                                             maxRows = 6,
                                                                                             sort = "desc",
                                                                                         }) => {
    const data = useMemo(() => {
        const parsed = (items || []).map((it) => ({
            id: it.vehicleId,
            name: `${it.plateNumber} • ${it.brand} ${it.model}`,
            value: Number.parseFloat(String(it.totalMaintenanceCost)) || 0,
        }));
        const ordered = parsed.sort((a, b) => (sort === "asc" ? a.value - b.value : b.value - a.value));
        return ordered.slice(0, maxRows);
    }, [items, maxRows, sort]);


    const noData = !loading && !error && data.length === 0;

    return (
        <div className={`rounded-2xl border bg-white shadow-sm ${className}`} style={{ minHeight: height }}>
            {/* Header */}
            <div className="p-4 flex items-center justify-between">
                <h3 className="font-semibold">{title}</h3>
                <div className="text-sm text-gray-600">{periodLabel}</div>
            </div>


            <div className="px-4 pb-6 relative">
                {loading ? (
                    <div className="p-6 text-sm text-gray-500">Cargando ranking…</div>
                ) : error ? (
                    <div className="p-6 text-sm text-rose-600">{error}</div>
                ) : noData ? (
                    <>
                        <div className="p-6 text-sm text-gray-500 opacity-40 blur-[2px] select-none">
                            (No hay elementos para mostrar)
                        </div>
                        <div className="absolute inset-0 z-10 flex items-center justify-center">
                            <div className="rounded-xl bg-white/80 px-4 py-2 text-sm text-gray-700 shadow">
                                No hay datos para el periodo seleccionado.
                            </div>
                        </div>
                    </>
                ) : (
                    <div style={{ height: height - 90 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={data}
                                layout="vertical"
                                margin={{ top: 8, right: 16, bottom: 0, left: 8 }}
                                barCategoryGap={12}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    type="number"
                                    tickLine={false}
                                    axisLine={false}
                                    tick={{ fontSize: 11 }}
                                    tickFormatter={(v) => fmtCurrency(Number(v))}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    width={220}
                                    tickLine={false}
                                    axisLine={false}
                                    tick={{ fontSize: 12 }}
                                />
                                <Tooltip
                                    cursor={{ opacity: 0.08 }}
                                    formatter={(v: any) => [fmtCurrency(Number(v)), "Coste total"]}
                                />
                                <Bar dataKey="value" radius={[8, 8, 8, 8]} fill={color}>
                                    <LabelList
                                        dataKey="value"
                                        position="right"
                                        formatter={(v: any) => fmtCurrency(Number(v))}
                                        className="text-[11px] fill-gray-700"
                                    />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </div>
    );
};